(() => {
  const ROOT_SELECTOR = '[data-body-types-carousel]';
  const MOBILE_QUERY = '(max-width: 767px)';

  class BodyTypesCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-body-types-viewport]');
      this.track = root.querySelector('[data-body-types-track]');
      this.previousButton = root.querySelector('[data-body-types-previous]');
      this.nextButton = root.querySelector('[data-body-types-next]');
      this.pagination = root.querySelector('[data-body-types-pagination]');
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.realIndex = 0;
      this.trackIndex = 0;
      this.animating = false;
      this.dragging = false;
      this.dragAxis = null;
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.transitionTimer = null;
      this.abortController = new AbortController();

      this.onMediaChange = this.refresh.bind(this);
      this.mediaQuery.addEventListener('change', this.onMediaChange);
      this.refresh();
    }

    get realSlides() {
      return Array.from(this.track.children).filter((slide) => !slide.hasAttribute('data-body-types-clone'));
    }

    refresh() {
      this.teardownMobile();
      if (this.mediaQuery.matches) this.setupMobile();
    }

    setupMobile() {
      this.mobileController = new AbortController();
      const signal = this.mobileController.signal;
      this.slides = this.realSlides;
      this.viewport.setAttribute('tabindex', '0');
      this.track.querySelectorAll('img').forEach((image) => { image.draggable = false; });
      this.realIndex = 0;
      this.root.classList.toggle('has-single-slide', this.slides.length <= 1);

      if (this.slides.length <= 1) {
        this.trackIndex = 0;
        this.setPosition(false);
        this.updateAccessibility();
        return;
      }

      const firstClone = this.createClone(this.slides[0]);
      const lastClone = this.createClone(this.slides[this.slides.length - 1]);
      this.track.prepend(lastClone);
      this.track.append(firstClone);
      this.trackIndex = 1;
      this.buildPagination();
      this.setPosition(false);
      this.updateAccessibility();

      this.previousButton.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishTransition();
      }, { signal });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.move(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.move(1); }
      }, { signal });
      this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this), { signal });
      this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this), { signal });
      this.viewport.addEventListener('pointerup', this.onPointerEnd.bind(this), { signal });
      this.viewport.addEventListener('pointercancel', this.onPointerEnd.bind(this), { signal });
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-body-types-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      Array.from(clone.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
      });
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('*').forEach((element) => Array.from(element.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
      }));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
      return clone;
    }

    buildPagination() {
      this.pagination.replaceChildren();
      this.slides.forEach((slide, index) => {
        if (!slide.id) slide.id = `body-type-${this.root.closest('.shopify-section')?.id || 'section'}-${index + 1}`;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-info-body-types__dot';
        dot.setAttribute('aria-label', `View body type ${index + 1}`);
        dot.setAttribute('aria-controls', slide.id);
        dot.addEventListener('click', () => this.goTo(index), { signal: this.mobileController.signal });
        this.pagination.append(dot);
      });
    }

    move(direction) {
      if (this.animating || this.slides.length <= 1) return;
      this.animating = true;
      this.realIndex = (this.realIndex + direction + this.slides.length) % this.slides.length;
      this.trackIndex += direction;
      this.setPosition(true);
      this.updateAccessibility();
      this.transitionTimer = window.setTimeout(() => this.finishTransition(), 450);
    }

    goTo(index) {
      if (this.animating || index === this.realIndex) return;
      this.animating = true;
      this.realIndex = index;
      this.trackIndex = index + 1;
      this.setPosition(true);
      this.updateAccessibility();
      this.transitionTimer = window.setTimeout(() => this.finishTransition(), 450);
    }

    setPosition(animate, dragOffset = 0) {
      this.track.classList.toggle('is-animating', animate);
      const width = this.viewport.clientWidth;
      this.track.style.transform = `translate3d(${(-this.trackIndex * width) + dragOffset}px, 0, 0)`;
    }

    finishTransition() {
      window.clearTimeout(this.transitionTimer);
      if (this.trackIndex === 0) this.trackIndex = this.slides.length;
      if (this.trackIndex === this.slides.length + 1) this.trackIndex = 1;
      this.setPosition(false);
      this.animating = false;
    }

    updateAccessibility() {
      this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === this.realIndex ? 'false' : 'true'));
      Array.from(this.pagination.children).forEach((dot, index) => dot.setAttribute('aria-current', index === this.realIndex ? 'true' : 'false'));
    }

    onPointerDown(event) {
      if (this.animating || this.slides.length <= 1 || event.button > 0) return;
      this.dragging = true;
      this.dragAxis = null;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.currentX = event.clientX;
      this.track.classList.remove('is-animating');
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      if (!this.dragAxis && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) this.dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (this.dragAxis !== 'x') return;
      event.preventDefault();
      this.currentX = event.clientX;
      this.setPosition(false, deltaX);
    }

    onPointerEnd(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.viewport.releasePointerCapture?.(event.pointerId);
      if (this.dragAxis !== 'x') { this.setPosition(false); return; }
      const distance = this.currentX - this.startX;
      if (Math.abs(distance) >= Math.min(60, this.viewport.clientWidth * 0.16)) this.move(distance < 0 ? 1 : -1);
      else {
        this.animating = true;
        this.setPosition(true);
        this.transitionTimer = window.setTimeout(() => this.finishTransition(), 450);
      }
    }

    teardownMobile() {
      window.clearTimeout(this.transitionTimer);
      this.mobileController?.abort();
      this.track.querySelectorAll('[data-body-types-clone]').forEach((clone) => clone.remove());
      this.realSlides.forEach((slide) => slide.removeAttribute('aria-hidden'));
      this.pagination.replaceChildren();
      this.viewport.setAttribute('tabindex', '-1');
      this.track.classList.remove('is-animating');
      this.track.style.removeProperty('transform');
      this.root.classList.remove('has-single-slide');
      this.animating = false;
      this.dragging = false;
    }

    destroy() {
      this.teardownMobile();
      this.mediaQuery.removeEventListener('change', this.onMediaChange);
      this.abortController.abort();
      delete this.root.bodyTypesCarousel;
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR) ? [scope] : Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => {
      root.bodyTypesCarousel?.destroy();
      root.bodyTypesCarousel = new BodyTypesCarousel(root);
    });
  };

  if (!window.bodyTypesCarouselEventsBound) {
    window.bodyTypesCarouselEventsBound = true;
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => root.bodyTypesCarousel?.destroy()));
    document.addEventListener('shopify:block:select', (event) => initialize(event.target.closest('.shopify-section') || document));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
